import { v4 as uuidv4 } from "uuid";
import { Dispatch, SetStateAction } from "react";
import type { CategoriesOfIngredients, ComponentCategory, PerfumeIngredient } from "@/models";
import type { EditPerfumeViewModel, EditComponentViewModel } from "./EditPerfumeViewModel";
import type { IngredientLots } from "@/repositories";

export interface PerfumeEditor {
  updatePerfumeViewModel: Dispatch<SetStateAction<EditPerfumeViewModel>>;
  ingredientDialogOpen: IngredientDialogOpen;
  setIngredientDialogOpen: Dispatch<SetStateAction<IngredientDialogOpen>>;
  volumeDialogOpen: VolumeDialogOpen;
  setVolumeDialogOpen: Dispatch<SetStateAction<VolumeDialogOpen>>;
  availableIngredients: CategoriesOfIngredients;
  perfumeViewModel: EditPerfumeViewModel;
  totalVolume: number;
  ingredientLots: IngredientLots;
  lockPerfume: () => Promise<void>;
  saveError: boolean;
}

export type IngredientDialogOpen = {
  componentId: string;
} | null;

export type VolumeDialogOpen = {
  componentId: string;
} | null;

function updatePerfumeComponent(
  editor: PerfumeEditor,
  componentId: string,
  edit: (component: EditComponentViewModel) => EditComponentViewModel,
) {
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => {
    const components = vm.components.map((component) =>
      component.componentId === componentId ? edit(component) : component,
    );

    return {
      ...vm,
      components,
    };
  });
}

export function addComponentWithDialog(editor: PerfumeEditor, category: ComponentCategory) {
  const newComponent: EditComponentViewModel = {
    componentId: uuidv4(),
    category: category,
    ingredient: null,
    displayName: "",
    volume: 0,
    numeroDeLot: null,
    suggested: false,
  };
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => ({
    ...vm,
    components: [...vm.components, newComponent],
  }));
  openComponentIngredientDialog(editor, newComponent.componentId);
}

export function addComponent(
  editor: PerfumeEditor,
  {
    category,
    ingredient,
    volume,
    suggested,
  }: {
    category: ComponentCategory;
    ingredient: PerfumeIngredient;
    volume: number;
    suggested: boolean;
  },
) {
  const newComponent: EditComponentViewModel = {
    componentId: uuidv4(),
    category,
    ingredient,
    displayName: "",
    volume: Math.min(100, Math.max(0, volume)),
    numeroDeLot: editor.ingredientLots[ingredient.name] ?? null,
    suggested,
  };
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => ({
    ...vm,
    components: [...vm.components, newComponent],
  }));
}

export function duplicateComponent(
  editor: PerfumeEditor,
  sourceComponentId: string,
  volume: number,
) {
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => {
    const source = vm.components.find((c) => c.componentId === sourceComponentId);
    if (!source) return vm;
    const newComponent: EditComponentViewModel = {
      componentId: uuidv4(),
      category: source.category,
      ingredient: source.ingredient,
      displayName: source.displayName,
      volume: Math.min(100, Math.max(0, volume)),
      numeroDeLot: source.numeroDeLot,
      suggested: source.suggested,
    };
    return { ...vm, components: [...vm.components, newComponent] };
  });
}

export function openComponentIngredientDialog(editor: PerfumeEditor, componentId: string) {
  editor.setIngredientDialogOpen({
    componentId: componentId,
  });
}

export function closeComponentIngredientDialog(editor: PerfumeEditor) {
  editor.setIngredientDialogOpen(null);
}

export function openComponentVolumeDialog(editor: PerfumeEditor, componentId: string) {
  editor.setVolumeDialogOpen({ componentId });
}

export function closeComponentVolumeDialog(editor: PerfumeEditor) {
  editor.setVolumeDialogOpen(null);
}

export function deleteComponent(editor: PerfumeEditor, componentIdToRemove: string) {
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => ({
    ...vm,
    components: vm.components.filter((c) => c.componentId !== componentIdToRemove),
  }));
}

export function editComponent(
  editor: PerfumeEditor,
  componentId: string,
  {
    displayName,
    ingredient,
    volume,
  }: {
    displayName?: string;
    ingredient?: PerfumeIngredient | null;
    volume?: number;
  },
) {
  updatePerfumeComponent(editor, componentId, (component) => {
    const newComponent = { ...component };

    if (displayName !== undefined) {
      newComponent.displayName = displayName;
    }

    if (ingredient !== undefined) {
      // another ingredient chosen in the picker: no longer a suggestion
      if (ingredient?.name !== component.ingredient?.name) {
        newComponent.suggested = false;
      }
      newComponent.ingredient = ingredient;
      newComponent.numeroDeLot = ingredient
        ? (editor.ingredientLots[ingredient.name] ?? null)
        : null;
    }

    if (volume !== undefined) {
      newComponent.volume = Math.min(100, Math.max(0, volume));
    }

    return newComponent;
  });
}

export function setPerfumeProperty(
  editor: PerfumeEditor,
  changes: {
    newName?: string;
    newEmail?: string;
    newTitle?: string;
    newUsername?: string;
    newUserLastName?: string;
    newComment?: string;
  },
) {
  editor.updatePerfumeViewModel((vm): EditPerfumeViewModel => ({
    ...vm,
    name: changes.newName ?? vm.name,
    email: changes.newEmail ?? vm.email,
    title: changes.newTitle ?? vm.title,
    username: changes.newUsername ?? vm.username,
    userLastName: changes.newUserLastName ?? vm.userLastName,
    comment: changes.newComment ?? vm.comment,
  }));
}
